/**
 * Mouvement du site — entrées dans l'écran.
 * Porté depuis preview/kb-react/src/hooks/useMotion.js.
 *
 * Les styles vivent dans critical.css ; ici on ne fait que poser des classes.
 *
 * Le contenu reste visible sans JS : c'est ce script qui ajoute `.js-mo` sur
 * <html>, et toutes les règles de masquage en dépendent. Un crawler, ou un
 * navigateur où le script échoue, voit la page entière.
 *
 * On ne balise rien à la main dans le markup : les cibles sont trouvées par
 * sélecteur, ce qui évite de toucher à chaque section quand on en ajoute une.
 */

/* Les titres donnent le départ du regard : ils montent un peu plus. */
const TITRES = 'h2, .hm-h, .pd-h, .hm-kick, .cat-h, .hm-sub';

/* Blocs révélés en cascade. */
const BLOCS = [
  '.hm-mo', // tuiles de la mosaïque
  '.hm-cs', // cartes du Club
  '.hm-post', // articles du Blogue
  '.hm-re2 > li', // garanties en grand
  '.hm-fav-car .cat-card',
  '.cat-card', // vignettes produit
  /* La liste du prototype couvre aussi le catalogue, les pages produit et le
     comparateur (.cat-sec > *, .pd-sec > *, .es-i, .cmpc-row, .rv…). Ces
     sélecteurs seront ajoutés avec les sections correspondantes — un
     sélecteur qui ne correspond à rien ne coûte rien, mais il ment sur ce
     que le thème contient. */
].join(', ');

/* Un élément qu'on ne révélera jamais ne doit pas être masqué : les cartes
   d'une piste sortent du cadre horizontalement, l'observateur ne les
   croisera donc pas et elles resteraient invisibles pour toujours. Elles
   gardent le survol, pas l'entrée. */
const HORS_CADRE = '.hm-fav-car, .hm-mag-grid, .hm-deal-rail, .hm-qflow, .hm-band2';

function reduit() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function animer() {
  if (reduit() || !('IntersectionObserver' in window)) return;
  document.documentElement.classList.add('js-mo');

  const vus = new Set();
  const obs = new IntersectionObserver(
    (entrees) => {
      for (const e of entrees) {
        if (!e.isIntersecting) continue;
        e.target.classList.add('is-in');
        obs.unobserve(e.target); // une entrée ne se rejoue pas
      }
    },
    { threshold: 0.12, rootMargin: '0px 0px -8% 0px' },
  );

  const revelable = (el) =>
    !el.classList.contains('sr-only') && !el.classList.contains('sr') && !el.closest(HORS_CADRE);

  const marquer = (el, classe, rang) => {
    if (vus.has(el) || !revelable(el)) return;
    vus.add(el);
    el.classList.add(classe);
    /* cascade plafonnée à 6 pas : au-delà le bas d'une grille se ferait attendre */
    if (rang) el.style.setProperty('--mo-i', Math.min(rang, 6));
    obs.observe(el);
  };

  document.querySelectorAll(TITRES).forEach((el) => marquer(el, 'mo-t', 0));

  /* Les blocs se décalent à l'intérieur de leur propre parent : deux grilles
     côte à côte démarrent chacune à zéro. */
  const parParent = new Map();
  document.querySelectorAll(BLOCS).forEach((el) => {
    const p = el.parentElement;
    const rang = parParent.get(p) ?? 0;
    parParent.set(p, rang + 1);
    marquer(el, 'mo-in', rang);
  });

  /* Au-dessus de la ligne de flottaison, rien n'est masqué : le hero et la
     bande doivent être là au premier rendu, c'est le LCP de la page. */
  document.querySelectorAll('.mo-t, .mo-in').forEach((el) => {
    if (el.getBoundingClientRect().top < window.innerHeight * 0.9) {
      el.classList.add('is-in');
      obs.unobserve(el);
    }
  });

  /* Filet de sécurité : si un élément n'a jamais croisé l'écran — mise en
     page inattendue, conteneur qui défile, onglet resté en fond — il serait
     masqué indéfiniment. Au bout de 2,5 s tout ce qui reste caché est révélé
     sans condition : du contenu invisible est un défaut plus grave qu'une
     animation manquée. */
  setTimeout(() => {
    document.querySelectorAll('.mo-t:not(.is-in), .mo-in:not(.is-in)').forEach((el) => {
      el.classList.add('is-in');
      obs.unobserve(el);
    });
  }, 2500);
}

requestAnimationFrame(animer);

/* L'éditeur de thème remplace le HTML d'une section : ses éléments n'ont
   plus ni classe ni observateur. On les reprend, sans retoucher au reste. */
document.addEventListener('shopify:section:load', (event) => {
  if (reduit() || !('IntersectionObserver' in window)) return;
  event.target.querySelectorAll('.mo-t, .mo-in').forEach((el) => el.classList.add('is-in'));
});
